import { ChangeEvent, useEffect, useRef, useState } from "react"
import Api from "../api/api"

// mirrors MIN_SKU_IMAGE_WIDTH in lib/api/skus.ts — the backend rejects
// narrower uploads too
export const MIN_IMAGE_WIDTH = 512

export interface ImageField {
  // the staged upload: the picked PNG, or (logos) the blob rendered by the
  // placement editor
  file: Blob | null
  width: number | null
  previewUrl: string | null
  error: string
  pick: (e: ChangeEvent<HTMLInputElement>) => void
  // stage an already-prepared PNG (the logo editor's output) with its width
  stage: (blob: Blob, width: number) => void
  clear: () => void
  fail: (message: string) => void
  fileInputRef: React.RefObject<HTMLInputElement | null>
  // presign + PUT the staged file; call only with a file staged. entityId
  // is the EXISTING row the image belongs to (sku / product id — its id is
  // part of the key, so Add dialogs create the row first)
  upload: (entityId: string) => Promise<{ width: number, rev: string }>
}

export interface ImageFieldOptions {
  // intercepts a picked file instead of staging it (the logo flow opens the
  // placement editor, which stages its result via `stage`)
  onPick?: (file: File) => void
}

// All image-picking state for a dialog: staged PNG with a natural-width
// check, object-URL preview, and the presign→PUT upload flow. Shared by
// AddSkuDialog and EditSkuDialog; `presign` swaps the sku screenshot
// presign for another endpoint (org logos, product images, sku logos) and
// receives upload()'s entityId.
export function useImageField(accessToken: string, presign?: (width: number, entityId: string) => Promise<{ uploadUrl: string, rev: string }>, options?: ImageFieldOptions): ImageField {
  const [file, setFile] = useState<Blob | null>(null)
  const [width, setWidth] = useState<number | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState("")
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  const pick = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0]
    e.target.value = ""   // allow re-picking the same file
    if (!picked) return
    setError("")
    if (options?.onPick) {
      options.onPick(picked)
      return
    }
    const url = URL.createObjectURL(picked)
    const img = new Image()
    img.onload = () => {
      if (img.naturalWidth < MIN_IMAGE_WIDTH) {
        URL.revokeObjectURL(url)
        setError(`Image must be at least ${MIN_IMAGE_WIDTH}px wide — this one is ${img.naturalWidth}px.`)
        return
      }
      setFile(picked)
      setWidth(img.naturalWidth)
      setPreviewUrl(url)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      setError("That file doesn't look like an image.")
    }
    img.src = url
  }

  const stage = (blob: Blob, blobWidth: number) => {
    setError("")
    setFile(blob)
    setWidth(blobWidth)
    setPreviewUrl(URL.createObjectURL(blob))
  }

  const clear = () => {
    setFile(null)
    setWidth(null)
    setPreviewUrl(null)
    setError("")
  }

  const fail = (message: string) => setError(message)

  const upload = async (entityId: string): Promise<{ width: number, rev: string }> => {
    const { uploadUrl, rev } = presign !== undefined
      ? await presign(width!, entityId)
      : await Api.createSkuImageUpload({ skuId: entityId, width: width!, accessToken })
    // both headers are part of the presign signature and must match it
    // exactly (the revisioned key makes the immutable Cache-Control safe)
    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "image/png",
        "Cache-Control": "public, max-age=31536000, immutable"
      },
      body: file!
    })
    if (!response.ok) throw new Error(`Image upload failed (HTTP ${response.status})`)
    return { width: width!, rev }
  }

  return { file, width, previewUrl, error, pick, stage, clear, fail, fileInputRef, upload }
}

// The image field: staged-file preview with Remove, or — in the edit
// dialog, via `current` — the sku's existing image with Replace/Remove links
// (removal only takes effect on save; Undo restores it). The "logo" variant
// previews at the real title height (the normalized canvas makes that
// meaningful) and offers Adjust… to reopen the placement editor.
export function ImageFieldView({ fields, busy, current, label = "Screenshot (PNG)", hint, variant, onAdjust }: {
  fields: ImageField
  busy: boolean
  current?: {
    imageUrl?: string
    removed: boolean
    onRemove: () => void
    onRestore: () => void
  }
  label?: string
  hint?: string
  variant?: "logo"
  onAdjust?: () => void
}) {
  const f = fields
  const linkStyle = { background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "10pt" } as const
  const showCurrent = f.file === null && current !== undefined && current.imageUrl !== undefined && !current.removed
  const previewProps = variant === "logo"
    ? { className: "product-logo-title" }
    : { style: { maxWidth: 160, maxHeight: 120, borderRadius: 4 } }
  return (
    <div className="generate-codes-field">
      <span className="generate-codes-label">{label}</span>
      {f.previewUrl ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src={f.previewUrl} alt="" {...previewProps} />
          <span className="generate-codes-hint">{f.width}px wide</span>
          {onAdjust && (
            <button className="card-link" style={linkStyle} onClick={onAdjust} disabled={busy}>
              Adjust…
            </button>
          )}
          <button className="card-link" style={linkStyle} onClick={f.clear} disabled={busy}>
            Remove
          </button>
        </div>
      ) : showCurrent ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* original only, no srcset: right after a replacement the scaled
              variants may not exist yet, and the original is fine at 160px */}
          <img src={current.imageUrl} alt="" {...previewProps} />
          <button className="card-link" style={linkStyle} onClick={() => f.fileInputRef.current?.click()} disabled={busy}>
            Replace…
          </button>
          <button className="card-link-danger" style={linkStyle} onClick={current.onRemove} disabled={busy}>
            Remove
          </button>
        </div>
      ) : current?.removed ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span className="generate-codes-hint">Image will be removed on save</span>
          <button className="card-link" style={linkStyle} onClick={current.onRestore} disabled={busy}>
            Undo
          </button>
          <button className="card-link" style={linkStyle} onClick={() => f.fileInputRef.current?.click()} disabled={busy}>
            Choose image…
          </button>
        </div>
      ) : (
        <button
          className="card-link"
          style={{ ...linkStyle, fontSize: undefined, textAlign: "left" }}
          onClick={() => f.fileInputRef.current?.click()}
          disabled={busy}
        >
          Choose image…
        </button>
      )}
      <input
        ref={f.fileInputRef}
        type="file"
        accept="image/png"
        style={{ display: "none" }}
        onChange={f.pick}
      />
      {f.error && <span className="generate-codes-error">{f.error}</span>}
      <span className="generate-codes-hint">
        {hint ?? `At least ${MIN_IMAGE_WIDTH}px wide; scaled variants (128–1024px) are generated automatically after upload`}
      </span>
    </div>
  )
}
